'use client';

import type React from 'react';
import { useEffect, useRef } from 'react';

interface ScrollAnimationWrapperProps {
  children: React.ReactNode;
  animationType: 'fade-up' | 'flip-left';
  duration?: number; // Optional duration in seconds
  delay?: number; // Optional delay in seconds
  className?: string; // Allow passing additional class names
  once?: boolean; // Trigger animation only once
  amount?: number | 'some' | 'all'; // Amount of element in view to trigger animation
}

const ScrollAnimationWrapper: React.FC<ScrollAnimationWrapperProps> = ({
  children,
  animationType,
  duration = 0.8, // Default duration
  delay = 0, // Default delay
  className = '',
  once = true, // Default to trigger once
  amount = 0.3, // Default trigger amount
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    // Convert amount to a threshold value between 0 and 1
    let threshold = 0.3;
    if (typeof amount === 'number') {
      threshold = amount;
    } else if (amount === 'all') {
      threshold = 1.0;
    } else if (amount === 'some') {
      threshold = 0.1;
    }

    // Create intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add the is-visible class when element is in view
            entry.target.classList.add('is-visible');

            // If once is true, unobserve after animation is triggered
            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once) {
            // If not once, remove the class when out of view
            entry.target.classList.remove('is-visible');
          }
        });
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px', // Trigger slightly before element is in view
      },
    );

    // Start observing
    observer.observe(ref.current);

    // Clean up
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [once, amount]);

  // Determine which animation class to use
  const animationClass = animationType === 'fade-up' ? 'animate-when-visible' : 'animate-when-visible-flip';

  // Apply inline styles for custom duration and delay
  const style = {
    '--animation-duration': `${duration}s`,
    '--animation-delay': `${delay}s`,
  } as React.CSSProperties;

  return (
    <div ref={ref} className={`${animationClass} ${className}`} style={style}>
      {children}
    </div>
  );
};

export default ScrollAnimationWrapper;
